import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import { FaLightbulb, FaRegCalendar } from 'react-icons/fa';
import styled from 'styled-components';

import { CustomInput } from '@/components/utils/DatePicker/CustomInput';
import { INTERVAL_BUSINESS_DAYS, REMOTEST_MONTHS } from '@/constants';
import { Label } from '@/components/shared/Label';
import { ServiceEndingAndStartAnnouncement } from '@/components/pages/Subscription/Container/ServiceEndingAndStartAnnouncement';
import i18n from '@/settings/i18n';

export type Props = {
  minDate: Date;
  maxDate: Date;
  dateFormat: string;
  error?: string;
  startsOn: Date;
  setStartsOn: (date: Date) => void;
  serviceGroupId: number;
};

export const StartsOnView: React.FC<Props> = ({
  minDate,
  maxDate,
  startsOn,
  dateFormat,
  error,
  setStartsOn,
  serviceGroupId,
}) => {
  const { t } = useTranslation();
  return (
    <Styled>
      <div className="l-block">
        <div id="startsOn" className={classnames('field', { error })}>
          <label>{t('models.subscription.startsOn')}</label>
          <div className="date-picker">
            <DatePicker
              {...{ dateFormat, maxDate, minDate }}
              customInput={<CustomInput />}
              onChange={(date) => date instanceof Date && setStartsOn(date)}
              selected={startsOn}
              dayClassName={(date) => {
                const day = date.getDay();
                if (day === 0) return 'react-datepicker__day--sun';
                if (day === 6) return 'react-datepicker__day--sat';
                return null;
              }}
            />
            <FaRegCalendar className="calendar-icon" />
            {error && (
              <div>
                <Label>{error}</Label>
              </div>
            )}
            <div className="hint starts-on">
              <FaLightbulb />
              <div>
                <div>
                  <span>
                    {t('ui.expression.afterBusinessDays', {
                      num: INTERVAL_BUSINESS_DAYS + 1,
                    })}
                    &nbsp;
                    {t('ui.expression.inputableMonthRange', {
                      num: REMOTEST_MONTHS,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <ServiceEndingAndStartAnnouncement serviceGroupId={serviceGroupId} />
          {/* 年末年始のスケジュール通知 */}
          <div className="holidayNotice">
            <Trans i18nKey="ui.expression.newYearHolidayLink">
              <a
                href={
                  i18n.language === 'ja'
                    ? 'https://globis4biz.zendesk.com/hc/ja/articles/51998414337177'
                    : 'https://globis-business.zendesk.com/hc/en-us/articles/52188895964441'
                }
                target="_blank"
                rel="noopener noreferrer"
              />
            </Trans>
          </div>
          <div className="holidayNotice">
            <p>{t('ui.expression.newYearStartDayNotice')}</p>
          </div>
        </div>
      </div>
    </Styled>
  );
};

const Styled = styled.div`
  .l-block {
    margin-bottom: 3em;
  }

  .date-picker {
    position: relative;
    width: 100%;
  }

  .date-picker > div {
    width: 49%;
  }

  .calendar-icon {
    position: absolute;
    top: 12px;
    left: 44%;
    pointer-events: none;
  }

  .hint.starts-on {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* calender */
  .react-datepicker__day--sat {
    color: #5281ff;
  }

  .react-datepicker__day--sun {
    color: #c74444;
  }

  .react-datepicker__day--disabled.react-datepicker__day--sat,
  .react-datepicker__day--disabled.react-datepicker__day--sun {
    opacity: 0.3;
  }
  .react-datepicker__day--selected.react-datepicker__day--sat,
  .react-datepicker__day--selected.react-datepicker__day--sun {
    color: #fff !important;
  }
  /* 年末年始のスケジュールリンクと通知スタイル */
  .holidayNotice {
    color: red;
    margin-bottom: 5px;
  }
`;
